import { LoadingIndicator } from "@light-technology/components";
import { useState } from "react";
import type { PlanResponse } from "../../state/api.types";
import type { ServiceTerritory, UsageKwh } from "../PlanEditPage/common";
import {
  SERVICE_TERRITORY_SHORT,
  rateComparisonToValues,
  usePlanComparison,
  usePlanComparisonValues,
} from "./common";
import { PlanComparisonDataTable } from "./PlanComparisonDataTable";

type PlanComparisonFromComparisonTableProps = {
  plan: PlanResponse;
  serviceTerritory: ServiceTerritory;
  planComparison: ReturnType<typeof usePlanComparison>;
  usageKwh?: UsageKwh;
  economicsOnly?: boolean;
};

export const PlanComparisonFromComparisonTable = ({
  plan,
  serviceTerritory,
  planComparison,
  usageKwh,
  economicsOnly,
}: PlanComparisonFromComparisonTableProps) => {
  const [lastData, setLastData] = useState(() => planComparison.data);
  if (planComparison.data && planComparison.data !== lastData) {
    setLastData(planComparison.data);
  }
  const displayData = planComparison.data ?? lastData;

  const planComparisonValues = usePlanComparisonValues({
    ...planComparison,
    data: displayData,
  } as ReturnType<typeof usePlanComparison>);
  const values = displayData
    ? [
        rateComparisonToValues(
          displayData.rate,
          displayData.monthly_commission_dollars,
        ),
        ...planComparisonValues,
      ]
    : [];

  if (
    !displayData &&
    (planComparison.isLoading || planComparison.isUninitialized)
  ) {
    return <LoadingIndicator type="brand-spinner" />;
  }
  if (planComparison.isError) {
    return (
      <div className="flex flex-col items-center gap-sm py-3xl text-center">
        <div className="text-sm font-medium text-primary">
          Unable to load competition data
        </div>
        <div className="text-sm text-tertiary">
          Please contact support if this problem persists.
        </div>
      </div>
    );
  }
  if (!displayData?.comparison_plans.length) {
    return (
      <div className="flex flex-col items-center gap-sm py-3xl text-center">
        <div className="text-sm font-medium text-primary">
          No competitors found
        </div>
        <div className="text-sm text-tertiary">
          Competitor plans for this territory and term will appear here.
        </div>
      </div>
    );
  }
  return (
    <PlanComparisonDataTable
      hasSolarBuyback={plan.has_solar_buyback}
      usageKwh={usageKwh}
      showAnnualSavings
      hideUniformTermLength
      economicsOnly={economicsOnly}
      hideCommission
      planNames={[
        "This plan",
        ...displayData.comparison_plans.map(
          (comparison) => `${comparison.provider} ${comparison.plan}`,
        ),
      ]}
      values={values}
      unavailableString={`Unavailable in ${SERVICE_TERRITORY_SHORT[serviceTerritory]}`}
    />
  );
};
